import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  Link,
  TextField,
  List,
  ListItem,
  ListItemText,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip
} from '@mui/material';
import { apiRequest } from '../utils/api';

export default function RobinhoodLogin() {
  const [status, setStatus] = useState('idle'); // idle, checking, authorized, unauthorized, error
  const [authorizationUrl, setAuthorizationUrl] = useState(null);
  const [error, setError] = useState(null);
  const [mcpUrl, setMcpUrl] = useState(null);
  const [tools, setTools] = useState([]);
  const [testResult, setTestResult] = useState(null);
  const [selectedTool, setSelectedTool] = useState('');
  const [toolArgs, setToolArgs] = useState({});

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    try {
      setStatus('checking');
      const response = await apiRequest('/api/mcp/status');
      setMcpUrl(response.data.url);
      if (response.data.authorized) {
        setStatus('authorized');
        loadTools();
      } else {
        setStatus('unauthorized');
      }
    } catch (err) {
      setStatus('error');
      setError(err.response?.data?.error || err.message);
    }
  };

  const loadTools = async () => {
    try {
      const response = await apiRequest('/api/mcp/tools');
      setTools(response.data.tools || []);
    } catch (err) {
      console.error('Failed to load tools:', err);
    }
  };

  const startAuth = async () => {
    try {
      setStatus('checking');
      const response = await apiRequest('/api/mcp/auth/start');
      if (response.data.authorized) {
        setStatus('authorized');
        loadTools();
      } else if (response.data.authorizationUrl) {
        setAuthorizationUrl(response.data.authorizationUrl);
        setStatus('unauthorized');
      }
    } catch (err) {
      setStatus('error');
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleOpenAuthUrl = () => {
    if (authorizationUrl) {
      window.open(authorizationUrl, '_blank');
    }
  };

  const testTool = async () => {
    try {
      setTestResult({ loading: true });
      const response = await apiRequest('/api/mcp/call', {
        method: 'POST',
        body: JSON.stringify({ name: selectedTool, args: toolArgs })
      });
      setTestResult({ success: true, data: response.data });
    } catch (err) {
      setTestResult({ error: err.response?.data?.error || err.message });
    }
  };

  const handleToolSelect = (toolName) => {
    setSelectedTool(toolName);
    setToolArgs({});
    setTestResult(null);
  };

  const handleArgChange = (paramName, value) => {
    setToolArgs(prev => ({ ...prev, [paramName]: value }));
  };

  const getSelectedTool = () => {
    return tools.find(t => t.name === selectedTool);
  };

  return (
    <Container maxWidth="md" sx={{ mt: { xs: 1, sm: 4 }, px: { xs: 1, sm: 3 } }}>
      <Paper sx={{ p: 4 }}>

        {mcpUrl && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              MCP Server: {mcpUrl}
            </Typography>
          </Box>
        )}

        {status === 'checking' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {status === 'authorized' && (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              Robinhood MCP is already authorized.
            </Alert>

            <Divider sx={{ my: 3 }} />
            <Typography variant="h5" gutterBottom>
              Test MCP Operations
            </Typography>

            {tools.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Available Tools:
                </Typography>
                <List dense>
                  {tools.map((tool) => (
                    <ListItem 
                      key={tool.name} 
                      button 
                      onClick={() => handleToolSelect(tool.name)}
                      selected={selectedTool === tool.name}
                    >
                      <ListItemText 
                        primary={tool.name} 
                        secondary={tool.description} 
                      />
                    </ListItem>
                  ))}
                </List>

                {selectedTool && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      {getSelectedTool()?.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {getSelectedTool()?.description}
                    </Typography>

                    {getSelectedTool()?.inputSchema && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Parameters:
                        </Typography>
                        {Object.entries(getSelectedTool().inputSchema.properties || {}).map(([paramName, paramSchema]) => (
                          <Box key={paramName} sx={{ mb: 2 }}>
                            <Typography variant="body2" gutterBottom>
                              {paramName}
                              {getSelectedTool().inputSchema.required?.includes(paramName) && (
                                <span style={{ color: 'red' }}> *</span>
                              )}
                            </Typography>
                            {paramSchema.enum ? (
                              <FormControl fullWidth>
                                <Select
                                  value={toolArgs[paramName] || ''}
                                  onChange={(e) => handleArgChange(paramName, e.target.value)}
                                  displayEmpty
                                >
                                  <MenuItem value="">Select...</MenuItem>
                                  {paramSchema.enum.map((value) => (
                                    <MenuItem key={value} value={value}>
                                      {value}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            ) : paramSchema.type === 'boolean' ? (
                              <FormControl fullWidth>
                                <Select
                                  value={toolArgs[paramName] !== undefined ? String(toolArgs[paramName]) : ''}
                                  onChange={(e) => handleArgChange(paramName, e.target.value === 'true')}
                                  displayEmpty
                                >
                                  <MenuItem value="">Select...</MenuItem>
                                  <MenuItem value="true">true</MenuItem>
                                  <MenuItem value="false">false</MenuItem>
                                </Select>
                              </FormControl>
                            ) : paramSchema.type === 'number' ? (
                              <TextField
                                fullWidth
                                type="number"
                                value={toolArgs[paramName] || ''}
                                onChange={(e) => handleArgChange(paramName, parseFloat(e.target.value))}
                                placeholder={`Enter ${paramName}`}
                              />
                            ) : (
                              <TextField
                                fullWidth
                                value={toolArgs[paramName] || ''}
                                onChange={(e) => handleArgChange(paramName, e.target.value)}
                                placeholder={`Enter ${paramName}`}
                              />
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}

                    <Button
                      variant="contained"
                      onClick={testTool}
                      disabled={testResult?.loading}
                      sx={{ mt: 2 }}
                    >
                      Execute Tool
                    </Button>

                    {testResult && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" gutterBottom>
                          Result:
                        </Typography>
                        {testResult.loading && <CircularProgress size={20} />}
                        {testResult.error && (
                          <Alert severity="error">{testResult.error}</Alert>
                        )}
                        {testResult.success && (
                          <Box sx={{ 
                            p: 2, 
                            bgcolor: 'grey.100', 
                            borderRadius: 1,
                            overflow: 'auto',
                            maxHeight: 300
                          }}>
                            <pre>{JSON.stringify(testResult.data, null, 2)}</pre>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </>
        )}

        {status === 'unauthorized' && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Robinhood MCP requires authorization. Click the button below to start the OAuth flow.
            </Alert>
            
            {authorizationUrl ? (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Authorization URL:
                </Typography>
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'grey.100', 
                  borderRadius: 1, 
                  mb: 2,
                  wordBreak: 'break-all',
                  fontSize: '0.875rem'
                }}>
                  {authorizationUrl}
                </Box>
                <Button 
                  variant="contained" 
                  onClick={handleOpenAuthUrl}
                  fullWidth
                >
                  Open Authorization URL
                </Button>
                <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                  After authorizing, the callback will complete automatically. You can then refresh this page to verify authorization.
                </Typography>
              </Box>
            ) : (
              <Button 
                variant="contained" 
                onClick={startAuth}
                disabled={status === 'checking'}
              >
                Start Authorization
              </Button>
            )}
          </>
        )}

        {status === 'error' && (
          <>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Button variant="outlined" onClick={checkStatus}>
              Retry
            </Button>
          </>
        )}

        <Box sx={{ mt: 4 }}>
          <Button variant="text" onClick={checkStatus}>
            Refresh Status
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}
